/**
 * Agent Icons Mapping
 * 
 * Maps agent names from backend to their corresponding icons.
 * Used to display agent icons in the chat thinking/reasoning UI.
 */

import {
    LifeBuoy01,
    Globe02,
    FileCheck02,
    File02,
    Route,
    Mail01,
    AlertCircle,
    Package,
    Atom02,
} from "@untitledui/icons";
import type { FC } from "react";

/**
 * Type for icon components from @untitledui/icons
 */
type IconComponent = FC<{ className?: string }>;

/**
 * Mapping of agent names (from backend) to their icons
 */
export const AGENT_ICON_MAP: Record<string, IconComponent> = {
    // Active agents
    'eve_care': LifeBuoy01,
    'eve_track': Globe02,
    'eve_quote': FileCheck02,
    'eve_read': File02,
    'eve_plan': Route,
    'eve_mailer': Mail01,
    'eve_alert': AlertCircle,
    'eve_trader': Atom02, // Using generic icon as TrendingUp is not available
    'eve_docs': File02,
    'eve_order_manager': Package,
    
    // Placeholder agents (use generic icon)
    'eve_demand': Atom02,
    'eve_procurement': Atom02,
    'eve_pay': Atom02,
};

/**
 * Default icon for unknown agents
 */
export const DEFAULT_AGENT_ICON = Atom02;

/**
 * Get icon for an agent by name
 * 
 * @param agentName - Agent name from backend (e.g., 'eve_care', 'EveCare')
 * @returns Icon component for the agent, or default icon if not found
 */
export function getAgentIcon(agentName: string | null | undefined): IconComponent {
    if (!agentName) {
        return DEFAULT_AGENT_ICON;
    }
    
    // Normalize agent name (handle both 'eve_care' and 'EveCare' formats)
    const normalizedName = agentName.toLowerCase().replace(/\s+/g, '_');
    
    return AGENT_ICON_MAP[normalizedName] || DEFAULT_AGENT_ICON;
}

/**
 * Get agent display name (human-readable)
 * 
 * @param agentName - Agent name from backend
 * @returns Human-readable agent name
 */
export function getAgentDisplayName(agentName: string | null | undefined): string {
    if (!agentName) {
        return 'Unknown Agent';
    }
    
    // Convert 'eve_care' to 'Eve Care'
    return agentName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Get agent color class (for styling)
 * 
 * @param agentName - Agent name from backend
 * @returns Tailwind color class
 */
export function getAgentColorClass(agentName: string | null | undefined): string {
    if (!agentName) {
        return 'text-tertiary';
    }
    
    const normalizedName = agentName.toLowerCase();
    
    const colorMap: Record<string, string> = {
        'eve_care': 'text-blue-400',
        'eve_track': 'text-green-400',
        'eve_quote': 'text-purple-400',
        'eve_read': 'text-orange-400',
        'eve_plan': 'text-indigo-400',
        'eve_mailer': 'text-cyan-400',
        'eve_alert': 'text-red-400',
        'eve_trader': 'text-yellow-400',
        'eve_docs': 'text-orange-400',
        'eve_order_manager': 'text-blue-500',
    };
    
    return colorMap[normalizedName] || 'text-tertiary';
}

